/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Check, ChevronsUpDown, LoaderCircle, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { Input } from "@/components/ui/input";

type ErpSkuProp = {
  placeholder: string;
  value: any;
  bindLabel: string;
  bindValue: string;
  emptyListMessage?: string;
  searchLabel?: string;
  onChange: (prop: string | string[]) => void;
  onSearch: (prop: string) => Promise<any>;
  onBlur: () => void;
  defaultValue: any[];
  onValueChange: (prop: any) => void;
  onAllValueChange?: (prop: any) => void;
  multiple?: boolean;
};

export const ErpSku = ({
  placeholder,
  searchLabel,
  emptyListMessage,
  value,
  onChange,
  onSearch,
  onBlur,
  bindLabel,
  bindValue,
  defaultValue,
  onValueChange,
  onAllValueChange,
  multiple = false,
}: // defaultValue,
ErpSkuProp) => {
  const [searchText, setSearchText] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [process, setProcess] = React.useState(false);
  const [list, setList] = React.useState<any>([]);

  React.useEffect(() => {
    if (list.length === 0) {
      setList(defaultValue);
    }
  }, []);

  const displayLabel = React.useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return value.map((itemValue: any) => {
        const option =
          list.find((option: any) => option?.[bindValue] === itemValue) ||
          defaultValue?.find(
            (option: any) => option?.[bindValue] === itemValue,
          );
        return option
          ? `${option?.[bindLabel]} / ${option?.[bindValue]}`
          : itemValue;
      });
    }

    const currentValue =
      list.find((option: any) => option?.[bindValue] === value) ||
      defaultValue?.[0];
    return currentValue
      ? `${currentValue?.[bindLabel]} / ${currentValue?.[bindValue]}`
      : value;
  }, [value, list, defaultValue]);

  const onChangeValue = (prop: any) => {
    onAllValueChange?.(prop);

    if (multiple) {
      const existingValues = Array.isArray(value) ? value : [];
      const valueId = prop?.[bindValue];
      const alreadySelected = existingValues.some(
        (item: any) => item === valueId,
      );
      const nextValues = alreadySelected
        ? existingValues.filter((item: any) => item !== valueId)
        : [...existingValues, valueId];
      onValueChange(prop);
      onChange(nextValues);
    } else {
      onValueChange(prop?.[bindLabel]);
      onChange(prop?.[bindValue]);
    }
  };

  React.useEffect(() => {
    if (searchText !== "") {
      onSearchHandle();
    }
  }, [searchText]);

  const onSearchHandle = async () => {
    setList([]);
    setProcess(true);
    const response: any = await onSearch(searchText);
    setProcess(false);
    if (response?.data) {
      setList(response.data);
    }
  };

  const isCurrentOptionIsFiltered = ({
    value,
    label,
  }: {
    value: string;
    label: string;
  }) => {
    const localLabel = label ? label.toLowerCase() : label;
    const localValue = value ? value.toLowerCase() : value;
    const localSearchText = searchText?.toLowerCase();

    return (
      localLabel?.includes(localSearchText) ||
      localValue?.includes(localSearchText) ||
      false
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild onBlur={onBlur}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto min-h-[40px] justify-between"
          >
            <span className="flex-1 text-left">
              {multiple && Array.isArray(displayLabel) ? (
                <span className="flex flex-wrap gap-1">
                  {displayLabel.map((label, idx) => (
                    <span
                      key={idx}
                      className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
                    >
                      {label}
                    </span>
                  ))}
                </span>
              ) : displayLabel ? (
                displayLabel
              ) : (
                <span className="opacity-50">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <div className="p-1">
              <div className="flex">
                <Button
                  onClick={() => {
                    onSearchHandle();
                  }}
                  className="absolute"
                  size={"icon"}
                  variant={"ghost"}
                >
                  <Search />
                </Button>
                <Input
                  className="!outline-none !border-none pl-10 focus:ring-0 focus:ring-offset-0"
                  onChange={(e) => setSearchText(e.target.value)}
                  value={searchText}
                  placeholder={searchLabel || "Search Options"}
                />
              </div>
            </div>

            <CommandList>
              <ConditionRenderer condition={list.length === 0}>
                <ConditionRenderer condition={process}>
                  <div className="flex items-center justify-center">
                    <LoaderCircle className="mr-2 h-6 w-6 animate-spin" />
                  </div>
                  <div className="text-sm px-3 pt-3">
                    {emptyListMessage || "No Options found."}
                  </div>
                </ConditionRenderer>
              </ConditionRenderer>
              <CommandGroup>
                {list.map((option: any) => {
                  const isCurrentItemExist = isCurrentOptionIsFiltered({
                    label: option?.[bindLabel],
                    value: option?.[bindValue],
                  });
                  if (!isCurrentItemExist) {
                    return null;
                  }
                  return (
                    <CommandItem
                      key={option?.[bindValue]}
                      value={option}
                      onSelect={() => {
                        onChangeValue(option);
                        if (!multiple) {
                          setOpen(false);
                        }
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          multiple && Array.isArray(value)
                            ? value.includes(option?.[bindValue])
                              ? "opacity-100"
                              : "opacity-0"
                            : value === option?.[bindValue]
                              ? "opacity-100"
                              : "opacity-0",
                        )}
                      />
                      {option?.[bindLabel] || ""}
                      {option?.[bindLabel] && option?.[bindValue] ? " / " : ""}
                      {option?.[bindValue]}
                      {option?.["erp_uom"] ? " / " + option?.["erp_uom"] : ""}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
